import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  ErrorState,
  Field,
  Form,
  FullPageSpinner,
  Icon,
  PageHeader,
  Select,
} from "@/components/ui";
import { identity, support } from "@/lib/api";
import { formatDate, timeAgo } from "@/lib/format";
import { supportContextTitle, type SupportContext } from "@/lib/supportContext";
import type { SupportFieldResponse, SupportForm, SupportSubmissionSummary } from "@/lib/types";

import { FieldRenderer, isFieldVisible, type Answers } from "./fields";

const RESPONSE_HOURS = 48;
const DRAFT_KEY = "support.draft.idempotency-key";

function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** One idempotency key per in-progress draft, reused across retries and replaced once it succeeds or the
 * person starts over — so a retried submit after a dropped connection can never create two tickets. */
function useDraftIdempotencyKey(): [string, () => void] {
  const [key, setKey] = useState(() => sessionStorage.getItem(DRAFT_KEY) ?? newIdempotencyKey());
  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, key);
  }, [key]);
  const reset = () => {
    const next = newIdempotencyKey();
    sessionStorage.setItem(DRAFT_KEY, next);
    setKey(next);
  };
  return [key, reset];
}

function answersToResponses(form: SupportForm, answers: Answers): SupportFieldResponse[] {
  const responses: SupportFieldResponse[] = [];
  for (const f of form.fields) {
    const value = answers[f.id];
    if (isFieldVisible(f, answers) && value) responses.push({ field_id: f.id, value });
  }
  return responses;
}

function SuccessPanel({ reference, onDone }: { reference: string; onDone: () => void }) {
  return (
    <Card className="p-6">
      <div className="flex flex-col items-start gap-3">
        <h2 className="text-lg font-semibold text-fg">Your request has been sent</h2>
        <p className="text-sm text-fg-muted">
          Your reference is <span className="font-mono font-semibold text-fg">{reference}</span>. Our support
          team will respond within {RESPONSE_HOURS} hours. We've also sent this to your e-mail address.
        </p>
        <Button variant="brand" size="lg" onClick={onDone}>
          Back to my requests
        </Button>
      </div>
    </Card>
  );
}

const STATUS_TONE: Record<string, "neutral" | "success" | "warning" | "info"> = {
  open: "info",
  in_progress: "warning",
  pending: "warning",
  resolved: "success",
  closed: "neutral",
};

function statusLabel(status: string): string {
  return status.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The requester's own submission history (reference, subject, status, last updated). Ticket detail and
 * comments are a separate, larger feature (PHP-3773's scope is the submission form and this summary). */
function RequestList({ onNew }: { onNew: () => void }) {
  const submissions = useQuery({ queryKey: ["support", "submissions"], queryFn: support.submissions });

  if (submissions.isPending) return <FullPageSpinner />;
  if (submissions.isError) {
    return <ErrorState error={submissions.error} onRetry={() => void submissions.refetch()} />;
  }

  const rows = submissions.data.submissions;

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Icon name="mail" size={20} />}
          title="No requests yet"
          description="When you contact support, your requests will show up here."
          action={
            <Button variant="brand" size="lg" onClick={onNew}>
              + New request
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="brand" size="lg" onClick={onNew}>
          + New request
        </Button>
      </div>
      <Card className="overflow-hidden">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div className="overflow-x-auto" role="region" tabIndex={0} aria-label="Your support requests">
          <table className="w-full min-w-[480px] text-left">
            <thead className="bg-brand-50 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
              <tr>
                <th className="px-[22px] py-2 font-semibold">Reference</th>
                <th className="py-2 font-semibold">Subject</th>
                <th className="py-2 font-semibold">Status</th>
                <th className="py-2 pr-[22px] font-semibold">Last updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border border-t border-border">
              {rows.map((row: SupportSubmissionSummary) => (
                <tr key={row.submission_id}>
                  <td className="px-[22px] py-3 font-mono text-[13px] font-semibold text-fg">
                    {row.ticket_reference ?? "—"}
                  </td>
                  <td className="py-3 text-[13.5px] text-fg">{row.title ?? "—"}</td>
                  <td className="py-3">
                    {row.status ? (
                      <Badge tone={STATUS_TONE[row.status] ?? "neutral"}>{statusLabel(row.status)}</Badge>
                    ) : (
                      <span className="text-[12.5px] text-fg-subtle">—</span>
                    )}
                  </td>
                  <td
                    className="py-3 pr-[22px] text-[12.5px] text-fg-muted"
                    title={formatDate(row.updated_at)}
                  >
                    {row.updated_at ? timeAgo(row.updated_at) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function NewRequestForm({
  initialTitle,
  onCancel,
  onSubmitted,
}: {
  initialTitle: string;
  onCancel: () => void;
  onSubmitted: (reference: string) => void;
}) {
  const me = useQuery({ queryKey: ["identity", "me"], queryFn: identity.me });
  const form = useQuery({ queryKey: ["support", "form"], queryFn: support.form });
  const [idempotencyKey, resetIdempotencyKey] = useDraftIdempotencyKey();
  const [title, setTitle] = useState(initialTitle);
  const [issueId, setIssueId] = useState("");
  const [answers, setAnswers] = useState<Answers>({});

  const submit = useMutation({
    mutationFn: () => {
      if (!form.data) throw new Error("The support form hasn't loaded yet");
      return support.submit(idempotencyKey, {
        title,
        issue_id: form.data.issues.length > 0 ? issueId || null : null,
        responses: answersToResponses(form.data, answers),
      });
    },
    onSuccess: (result) => {
      resetIdempotencyKey();
      onSubmitted(result.ticket_reference);
    },
  });

  if (form.isPending || me.isPending) return <FullPageSpinner />;
  if (form.isError) return <ErrorState error={form.error} onRetry={() => void form.refetch()} />;
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;

  const visibleFields = form.data.fields.filter((f) => isFieldVisible(f, answers));

  return (
    <Card className="p-6">
      <Form
        className="flex flex-col gap-4"
        error={submit.error}
        onSubmit={(e) => {
          e.preventDefault();
          if (!submit.isPending) submit.mutate();
        }}
      >
        <ErrorAlert error={submit.error} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" size="lg">
            {(control) => (
              <input
                {...control}
                readOnly
                value={me.data.display_name || me.data.email}
                className="w-full rounded-[10px] border border-border bg-muted p-[13px] text-[15px] text-fg-muted"
              />
            )}
          </Field>
          <Field label="Your e-mail" size="lg">
            {(control) => (
              <input
                {...control}
                readOnly
                value={me.data.email}
                className="w-full rounded-[10px] border border-border bg-muted p-[13px] text-[15px] text-fg-muted"
              />
            )}
          </Field>
        </div>

        <Field label="Subject" name="title" required size="lg">
          {(control) => (
            <input
              {...control}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              placeholder="A short summary of your request"
              className="w-full rounded-[10px] border border-border bg-surface p-[13px] text-[15px] text-fg placeholder:text-fg-subtle focus:border-brand-500 focus:outline-none"
            />
          )}
        </Field>

        {form.data.issues.length > 0 && (
          <Field label="What's this about?" name="issue_id" required size="lg">
            {(control) => (
              <Select {...control} value={issueId} onChange={(e) => setIssueId(e.target.value)}>
                <option value="">Choose one</option>
                {form.data.issues.map((issue) => (
                  <option key={issue.id} value={issue.id}>
                    {issue.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {visibleFields.map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            value={answers[field.id] ?? ""}
            onChange={(value) => setAnswers((prev) => ({ ...prev, [field.id]: value }))}
          />
        ))}

        <div className="flex gap-3">
          <Button type="submit" variant="brand" size="lg" loading={submit.isPending}>
            Submit request
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={onCancel}
            disabledReason={submit.isPending ? "Submitting your request…" : undefined}
          >
            ← Back to my requests
          </Button>
        </div>
      </Form>
    </Card>
  );
}

type View = { kind: "list" } | { kind: "new"; initialTitle: string } | { kind: "success"; reference: string };

/** A failure screen can hand off here with `state: { supportContext }` (see `GetHelpLink`): the form
 * opens straight away, Subject already carrying the resource and reference so the customer never
 * retypes what broke. */
function initialView(locationState: unknown): View {
  const context = (locationState as { supportContext?: SupportContext } | null)?.supportContext;
  return context ? { kind: "new", initialTitle: supportContextTitle(context) } : { kind: "list" };
}

export function SupportPage() {
  const qc = useQueryClient();
  const location = useLocation();
  const [view, setView] = useState<View>(() => initialView(location.state));

  const backToList = () => {
    setView({ kind: "list" });
    void qc.invalidateQueries({ queryKey: ["support", "submissions"] });
  };

  return (
    <div>
      <PageHeader
        title="Support"
        description={`Tell us what's going on. Our support team will respond within ${RESPONSE_HOURS} hours.`}
      />
      {view.kind === "list" && <RequestList onNew={() => setView({ kind: "new", initialTitle: "" })} />}
      {view.kind === "new" && (
        <NewRequestForm
          initialTitle={view.initialTitle}
          onCancel={backToList}
          onSubmitted={(reference) => setView({ kind: "success", reference })}
        />
      )}
      {view.kind === "success" && <SuccessPanel reference={view.reference} onDone={backToList} />}
    </div>
  );
}
